import { inject, injectable } from 'inversify';
import { makeAutoObservable, reaction } from 'mobx';
import { reformatPhone } from '@shared/utils/helpers/formatString';
import { queryClient } from '@shared/api/reactQuery';
import { notifications } from '@shared/utils/notifications';
import { PATH_AUTH } from '@app/routes/paths';
import { NavigateFunction } from 'react-router-dom';
import { type AuthService, AuthServiceIdentifier } from '@shared/types/mobx/services/auth';
import { getPutApiUsersIdResetPasswordMutationOptions } from '@/shared/api/orval/users/users';
import {
  getGetApiAuthGenerateOtpPhoneQueryOptions,
  getGetApiAuthVerifyOtpPhoneOtpQueryOptions
} from '@/shared/api/orval/auth/auth';
import { ResetPasswordViewModel } from '../types';

@injectable()
export class ResetPasswordViewModelImpl implements ResetPasswordViewModel {
  screen: 'otp' | 'changePassword' = 'otp';

  phone = '';

  isContacted = true;

  userId: number | null = null;

  otp: string | null = null;

  navigate: NavigateFunction | null = null;

  constructor(@inject(AuthServiceIdentifier) private authModule: AuthService) {
    makeAutoObservable(this);

    reaction(
      () => this.phone,
      async () => {
        await this.generateOtp();
      }
    );
  }

  verifyOtp = async (rawOtp: any) => {
    try {
      const otp = Object.entries(rawOtp)
        // eslint-disable-next-line no-nested-ternary
        .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
        .map((e) => e[1])
        .join('');
      const requestOptions = getGetApiAuthVerifyOtpPhoneOtpQueryOptions(reformatPhone(this.phone), otp, {
        query: {
          staleTime: 0,
          gcTime: 0
        }
      });
      const { code, result, message } = await queryClient.fetchQuery(requestOptions);

      if (code !== 0 || !result || !result?.userId) {
        notifications.error(message);
        return;
      }
      const { userId } = result;
      this.userId = userId;
      this.otp = otp;

      this.screen = 'changePassword';
    } catch (e) {
      notifications.error(e);
    }
  };

  changePassword = async (password: string) => {
    try {
      if (!this.userId || !this.otp) return;
      const mutationOptions = getPutApiUsersIdResetPasswordMutationOptions({
        mutation: {
          onSuccess: async () => {
            notifications.success('Новый пароль установлен');
            await this.authUser();
          },
          onError: (err) => {
            notifications.error(err);
          }
        }
      });

      const mutation = queryClient.getMutationCache().build(queryClient, mutationOptions);

      await mutation.execute({
        id: this.userId,
        data: {
          newPassword: password,
          confirmPassword: password,
          otp: this.otp
        }
      });
    } catch (e) {
      notifications.error(e);
    }
  };

  private generateOtp = async () => {
    const queryOptions = getGetApiAuthGenerateOtpPhoneQueryOptions(reformatPhone(this.phone), {
      query: {
        staleTime: 0,
        gcTime: 0
      }
    });

    await queryClient.fetchQuery(queryOptions);
  };

  private authUser = async () => {
    if (!this.userId || !this.navigate) return;

    queryClient.setQueryData(['userId'], this.userId);
    await this.authModule.setAuthData({ userId: this.userId });
    this.navigate(PATH_AUTH.selectRole);
  };
}
